import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { onAuthError, statusOf } from "@common/api";
import { createQueryClient, useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { logger, recordNavigation } from "@jkast90/kast-common/diagnostics";
import Banner from "./components/Banner";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { useAdminMode } from "./hooks/useAdminMode";
import { useSession } from "./hooks/useSession";
import { useTheme } from "./hooks/useTheme";
import AboutPage from "./pages/AboutPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import BugReportPage from "./pages/BugReportPage";
import HomePage from "./pages/HomePage";

export const queryClient = createQueryClient({ statusOf, subscribeToAuthErrors: onAuthError });

function Shell() {
  const session = useSession();
  useCacheScopedToUser(queryClient, session.user?.sub ?? null);
  const adminMode = useAdminMode(session.user);
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const section = pathname.split("/")[1];

  // The pages leading up to a bug report, deep links and back/forward included.
  useEffect(() => {
    recordNavigation(pathname);
  }, [pathname]);

  return (
    <>
      <Header session={session} adminMode={adminMode} />
      <Banner />
      <ErrorBoundary
        key={section}
        onError={(error, info) => logger.error(`[Crash] ${error.message}`, info.componentStack ?? "")}
        onReport={(crashNote) => navigate("/bug-report", { state: { crashNote } })}
        buttonClassName="btn"
        primaryButtonClassName="btn primary"
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/account" element={<AccountPage session={session} adminMode={adminMode} />} />
          <Route path="/admin" element={<AdminPage session={session} adminMode={adminMode} />} />
          <Route path="/bug-report" element={<BugReportPage session={session} adminMode={adminMode} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      <Footer session={session} theme={theme} />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
